import React from 'react';
import { 
  User, 
  login as apiLogin, 
  register as apiRegister, 
  logout as apiLogout, 
  getCurrentUser, 
  getUserProfile, 
  isTokenValid,
  refreshAccessToken,
  hasRefreshToken,
  getToken,
  saveUserData,
  ACCESS_TOKEN_KEY // Import the key from the service
} from '../services/auth.service';
import { USE_REAL_API } from '../services/api';

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  login: (username: string, password: string, rememberMe?: boolean) => Promise<void>;
  register: (name: string, username: string, password: string, passwordConfirmation: string) => Promise<void>;
  logout: () => void;
  updateUserProfile: (userData: Partial<User>) => Promise<User>;
}

// Create context with default values
const AuthContext = React.createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  isLoading: true,
  login: async () => { throw new Error('Not implemented'); },
  register: async () => { throw new Error('Not implemented'); },
  logout: () => {},
  updateUserProfile: async (userData: Partial<User>) => { throw new Error('Not implemented'); }
});

// Props for AuthProvider
interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = React.useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);

  // Функция обновления токена
  const refreshToken = async () => {
    try {
      const newToken = await refreshAccessToken();
      return !!newToken;
    } catch (error) {
      console.error('Ошибка при обновлении токена:', error);
      return false;
    }
  };

  // Initialize auth state by checking for existing token and user data
  React.useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      try {
        // Try to get current user data from localStorage
        const storedUser = getCurrentUser();
        
        // If we have stored user data
        if (storedUser) {
          // Check if we have a valid token
          const token = getToken();
          
          // If token is valid (not expired)
          if (token && isTokenValid(token)) {
            setUser(storedUser);
            setIsAuthenticated(true);
            
            // If using real API, refresh user data from server
            if (USE_REAL_API) {
              try {
                const freshUserData = await getUserProfile();
                setUser(freshUserData);
              } catch (error) {
                console.error("Не удалось обновить данные пользователя:", error);
                // Don't log out if this fails - just use cached data
              }
            }
          } else if (hasRefreshToken()) {
            // If token is invalid but we have a refresh token
            try {
              await refreshAccessToken();
              const refreshedUser = getCurrentUser();
              if (refreshedUser) {
                setUser(refreshedUser);
                setIsAuthenticated(true);
              } else {
                // Clear auth data if refresh failed
                apiLogout();
                setIsAuthenticated(false);
                setUser(null);
              }
            } catch (refreshError) {
              console.error("Не удалось обновить токен:", refreshError);
              apiLogout();
              setIsAuthenticated(false);
              setUser(null);
            }
          } else {
            // No valid token and no refresh token
            apiLogout();
            setIsAuthenticated(false);
            setUser(null);
          }
        } else {
          // No stored user data
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        console.error("Ошибка инициализации авторизации:", error);
        // Ensure we're logged out if there's any error
        apiLogout();
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  // Автоматическое обновление токена до его истечения
  React.useEffect(() => {
    if (!isAuthenticated || !USE_REAL_API) return;

    // Функция для проверки и обновления токена
    const checkAndRefreshToken = async () => {
      // Если токен близок к истечению (< 5 минут), обновляем
      const token = getToken();
      if (token && !isTokenValid(token)) {
        await refreshToken();
      }
    };

    // Проверяем каждые 4 минуты
    const interval = setInterval(checkAndRefreshToken, 4 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const login = async (username: string, password: string, rememberMe?: boolean) => {
    try {
      const userData = await apiLogin({ username, password, rememberMe });
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      console.error("Ошибка входа:", error);
      throw error;
    }
  };

  const register = async (name: string, username: string, password: string, passwordConfirmation: string) => {
    try {
      const userData = await apiRegister({ name, username, password, passwordConfirmation });
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      console.error("Ошибка регистрации:", error);
      throw error;
    }
  };

  const logout = () => {
    apiLogout();
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateUserProfile = async (userData: Partial<User>): Promise<User> => {
    if (!user) {
      throw new Error("Пользователь не авторизован");
    }

    setIsLoading(true);
    try {
      // In a real app, this would call an API
      const updatedUser = { ...user, ...userData };
      
      // If using mock data in development mode
      if (!USE_REAL_API) {
        // Wait to simulate API call
        await new Promise(resolve => setTimeout(resolve, 800));
        saveUserData(updatedUser);
        setUser(updatedUser);
        return updatedUser;
      }
      
      // If using real API
      // const response = await api.patch('/user/profile', userData);
      // const updatedUser = response.data;
      // saveUserData(updatedUser);
      // setUser(updatedUser);
      // return updatedUser;
      
      // Temporary for development
      saveUserData(updatedUser);
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      console.error("Ошибка при обновлении профиля:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    isAuthenticated,
    user,
    isLoading,
    login,
    register,
    logout,
    updateUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => React.useContext(AuthContext);

// Backwards-compatible alias (in case some files import the misspelled name)
export const huseAuth = useAuth;